/**
 * Preparation-request helpers — deadline, urgency, column placement, and the
 * separated prep-request metrics.
 *
 * CRITICAL GUARDRAIL (CLAUDE.md §10 / §21.1 / §25.6): preparation requests are
 * a SEPARATE record type from files and must NEVER be counted in any file
 * statistic (Avg. Days, Bonus Qualifying, Monthly Bonus tiers). Everything in
 * this module operates ONLY over preparation_requests rows — it never touches
 * files — and the file stat helpers in dashboardStats.js never receive prep
 * rows. The two data sources stay completely separate; this module exists so
 * prep metrics have their own home that can't be confused with file stats.
 */

import { previousBusinessDay } from './businessDays';
import { getTodaySASTDateString } from './dateHelpers';
import { isInSelectedMonth } from './dashboardStats';
import { countTopLevelTasks, countTopLevelDone } from './prepTasks';

// Prep requests only ever appear in these three pipeline columns. They must
// NEVER appear in the file-only columns (paused, sent_to_sign, signed).
export const PREP_PIPELINE_COLUMNS = ['submitted', 'in_progress', 'completed'];

// Kept here, not in a component: the view's status toast (moveTo, bound for prepPipelineService in P6) and PrepMetaStrip both read them, and a service must never import from a presentational component.
// Keys are the INTERNAL status values stored in the database (never renamed);
// the values are DISPLAY text only — 'submitted' renders as "Inbox".
export const STATUS_LABELS = {
  submitted: 'Inbox',
  in_progress: 'In Progress',
  completed: 'Completed',
};

export const STATUS_COLOURS = {
  submitted: '#1B9AAA',
  in_progress: '#2EC4B6',
  completed: '#0F4C75',
};

/**
 * Deadline = the previous business day before the appointment date, using the
 * SAME helper the bonus clock relies on (no re-implementation). Returns a Date
 * pinned to 12:00 UTC, or null.
 */
export function computePrepDeadline(prep) {
  if (!prep || !prep.appointmentDate) return null;
  // Test records (is_test = true, CLAUDE.md §33) have no deadline — the
  // calculation is skipped entirely so no due-date or overdue treatment
  // ever applies to them.
  if (prep.isTest === true) return null;
  return previousBusinessDay(prep.appointmentDate);
}

/** YYYY-MM-DD key (UTC parts) for a deadline Date produced above. */
function dateKey(date) {
  if (!date) return null;
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`;
}

/** Today + tomorrow as SAST YYYY-MM-DD strings. */
function todayAndTomorrow() {
  const today = getTodaySASTDateString();
  const [y, m, d] = today.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + 1));
  const tomorrow = `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
  return { today, tomorrow };
}

/**
 * Urgency classification for a prep card's deadline:
 *   'overdue' — deadline already passed and not yet completed
 *   'soon'    — deadline is today or tomorrow and not yet completed
 *   'normal'  — otherwise (incl. anything already completed)
 */
export function getPrepUrgency(prep) {
  if (!prep || prep.status === 'completed') return 'normal';
  // Test records never show overdue / due-soon highlighting (CLAUDE.md §33).
  if (prep.isTest === true) return 'normal';
  const key = dateKey(computePrepDeadline(prep));
  if (!key) return 'normal';
  const { today, tomorrow } = todayAndTomorrow();
  if (key < today) return 'overdue';
  if (key === today || key === tomorrow) return 'soon';
  return 'normal';
}

/** True iff the prep is active (not completed) and its deadline has passed. */
export function isPrepOverdue(prep) {
  return getPrepUrgency(prep) === 'overdue';
}

// Statuses in which a request's task list may still be amended (26.7):
// the first column (Inbox = stored status 'submitted') and In Progress.
// Written as an explicit allow-list so any future status is non-amendable
// by default.
export const PREP_AMENDABLE_STATUSES = ['submitted', 'in_progress'];

/**
 * True when `email` is the person who LOADED this preparation request.
 *
 * The originator is `created_by` — set from the submitting user's email at
 * creation. Requests created before the Administration Assistant could load
 * them are all Adviser-originated, so no backfill is needed (26.1).
 */
export function isPrepOriginator(prep, email) {
  const owner = (prep?.createdBy || '').trim().toLowerCase();
  const who = (email || '').trim().toLowerCase();
  return owner.length > 0 && owner === who;
}

/**
 * May this ACTIVE role open this preparation request? (App-level gating,
 * CLAUDE.md 30 — RLS is untouched.)
 *
 *   Adviser                  → any request
 *   New Business Assistant   → any request (she works them all)
 *   Administration Assistant → only requests HE originated (26.1)
 */
export function canOpenPrepRequest(prep, role, email) {
  if (role === 'adviser' || role === 'new_business_assistant') return true;
  if (role === 'assistant') return isPrepOriginator(prep, email);
  return false;
}

/**
 * May this ACTIVE role amend this request's tasks? (26.7 rules unchanged —
 * Inbox/In Progress only, completed tasks locked, audit trail, and the
 * acknowledgement gate raised for the New Business Assistant.)
 *
 *   Adviser                  → any amendable request
 *   Administration Assistant → only amendable requests HE originated
 *   New Business Assistant   → never (she ticks tasks off; she never amends)
 */
export function canAmendPrepTasks(prep, role, email) {
  if (!prep || !PREP_AMENDABLE_STATUSES.includes(prep.status)) return false;
  if (role === 'adviser') return true;
  if (role === 'assistant') return isPrepOriginator(prep, email);
  return false;
}

/**
 * True when the task list was amended after submission and the New Business
 * Assistant has not acknowledged that round yet (CLAUDE.md §26.8).
 *
 * Comparing the two timestamps is what makes acknowledgement
 * per-amendment-round: a later amendment pushes `tasksAmendedAt` past the
 * stored acknowledgement and re-raises the flag by itself, so an earlier
 * acknowledgement can never cover a newer change.
 *
 * Display/gating only — this touches no statistic, deadline or SLA value.
 */
export function hasUnacknowledgedAmendment(prep) {
  if (!prep || !prep.tasksAmendedAt) return false;
  if (!prep.tasksAmendmentAckAt) return true;
  return new Date(prep.tasksAmendmentAckAt) < new Date(prep.tasksAmendedAt);
}

/**
 * Task completion progress for a prep request: { done, total } — counting
 * TOP-LEVEL tasks only (sub-tasks roll up into their parent's tick, §28.4).
 * Every "N tasks" / "X / N done" badge, the sealed Inbox card and the
 * unticked-tasks confirm read this one helper, which routes through
 * countTopLevelTasks / countTopLevelDone in prepTasks.js. Prep-request
 * display only — never a file statistic (§21.1).
 */
export function prepTaskProgress(prep) {
  const tasks = Array.isArray(prep?.tasks) ? prep.tasks : [];
  return { done: countTopLevelDone(tasks), total: countTopLevelTasks(tasks) };
}

/**
 * Format a 'YYYY-MM-DD' string as DD/MM/YYYY (SA convention).
 */
export function formatPrepYmd(ymd) {
  if (!ymd) return '—';
  const m = String(ymd).match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : ymd;
}

/**
 * Format a deadline Date (12:00 UTC) as DD/MM/YYYY from its UTC parts.
 */
export function formatPrepDeadline(date) {
  if (!date) return '—';
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}/${date.getUTCFullYear()}`;
}

/**
 * Format a deadline Date (12:00 UTC) as "DD/MM/YYYY (Wed)" — DD/MM/YYYY
 * from its UTC parts plus the en-ZA short weekday (computed in UTC so the
 * bracketed day matches the printed date). Example: "09/07/2026 (Thu)".
 *
 * Shared core for the prep detail view and the submission form, which each
 * wrap it with their own null-guard and (on the form) a "→ Deadline: "
 * prefix. Assumes a valid Date — callers guard for null.
 */
export function formatPrepDeadlineWeekday(date) {
  const dd = String(date.getUTCDate()).padStart(2, '0');
  const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
  const yyyy = date.getUTCFullYear();
  const day = new Intl.DateTimeFormat('en-ZA', { weekday: 'short', timeZone: 'UTC' }).format(date);
  return `${dd}/${mm}/${yyyy} (${day})`;
}

/**
 * Preparation requests for one pipeline column.
 *   - submitted / in_progress: status match, ordered by deadline ASC
 *     (most urgent first).
 *   - completed: status match AND completed_at in the selected month
 *     (same month-end rollover rule completed files obey), ordered by
 *     completed_at DESC (most recently completed first).
 * Any other column key returns [] — prep sheets never appear there.
 */
export function getPrepsForColumn(prepRequests, columnKey, filterMonth) {
  const list = Array.isArray(prepRequests) ? prepRequests : [];
  if (!PREP_PIPELINE_COLUMNS.includes(columnKey)) return [];

  if (columnKey === 'completed') {
    return list
      .filter((p) => p.status === 'completed' && isInSelectedMonth(p.completedAt, filterMonth))
      .sort((a, b) => {
        const at = a.completedAt ? new Date(a.completedAt).getTime() : 0;
        const bt = b.completedAt ? new Date(b.completedAt).getTime() : 0;
        if (bt !== at) return bt - at;
        return (b.id || '').localeCompare(a.id || '');
      });
  }

  return list
    .filter((p) => p.status === columnKey)
    .sort((a, b) => {
      const ad = computePrepDeadline(a);
      const bd = computePrepDeadline(b);
      const at = ad ? ad.getTime() : Infinity;
      const bt = bd ? bd.getTime() : Infinity;
      if (at !== bt) return at - bt;
      return (a.id || '').localeCompare(b.id || '');
    });
}

/**
 * Prep-request stat-row values for the New Business Assistant dashboard —
 * the ONLY view that renders prep metrics (the block was deliberately removed
 * from the Adviser dashboard and never existed on the Administration
 * Assistant's).
 *
 * Scoping mirrors how the file stat cards are scoped (CLAUDE.md §21.2):
 *   - submitted / inProgress: live counts of active prep sheets (active work
 *     is always visible regardless of month, like active files).
 *   - completed: completed_at in the selected month (same month-end rollover
 *     as the Completed file stat / File Completed column). EACH COMPLETED
 *     ROUND counts as one completion (26.10): prior rounds' completions
 *     count in their own months alongside the current round's.
 *   - overdue: live count — deadline passed and not yet completed.
 *
 * Operates ONLY over preparation_requests rows — never files (§27.6) — and,
 * per the §33 stats contract, test records (is_test = true) are excluded at
 * source.
 */
export function computePrepStats(prepRequests, filterMonth) {
  const list = (Array.isArray(prepRequests) ? prepRequests : []).filter(
    (p) => p && p.isTest !== true
  );
  let submitted = 0;
  let inProgress = 0;
  let completed = 0;
  let overdue = 0;
  for (const p of list) {
    if (p.status === 'submitted') submitted += 1;
    else if (p.status === 'in_progress') inProgress += 1;
    else if (p.status === 'completed' && isInSelectedMonth(p.completedAt, filterMonth)) completed += 1;
    // EACH COMPLETED ROUND COUNTS AS ONE COMPLETION (26.10): a request
    // reopened for a second round keeps its first round's completion in
    // that round's month, and completing round 2 adds a second completion.
    // Prior-round completions are month-scoped by their own completedAt.
    // This is the ONLY place the per-round rule applies; file statistics
    // never see prep rounds (21.1 / 27.6).
    for (const round of Array.isArray(p.priorRounds) ? p.priorRounds : []) {
      if (round && round.completedAt && isInSelectedMonth(round.completedAt, filterMonth)) {
        completed += 1;
      }
    }
    if (p.status !== 'completed' && isPrepOverdue(p)) overdue += 1;
  }
  return { submitted, inProgress, completed, overdue };
}

/**
 * Period figures for the unified statistics bar's Prep requests set
 * (CLAUDE.md §37), using the SAME period definitions as the file
 * "Submitted" and "Avg. Days" tiles (computeMonthStats, §21.2):
 *
 *   submitted — requests whose created_at (the submission timestamp)
 *               falls in the selected month (SAST), whatever their
 *               status now. Mirrors files' submitted_at-in-month.
 *   avgDays   — mean calendar days from created_at to completed_at over
 *               requests whose CURRENT completion falls in the selected
 *               month, formatted "X.X", or "—" when there are none.
 *               Mirrors the file Avg. Days (paused/gap time included).
 *
 * Completed stays with computePrepStats above (completed_at in the
 * selected month — the file tile's period — plus the §26.10 per-round
 * rule). This helper is separate so that function stays untouched.
 *
 * preparation_requests rows only, never files (§27.6); test records
 * (is_test = true, §33) excluded at source.
 */
export function computePrepPeriodStats(prepRequests, filterMonth) {
  const list = (Array.isArray(prepRequests) ? prepRequests : []).filter(
    (p) => p && p.isTest !== true
  );
  const submitted = list.filter((p) => isInSelectedMonth(p.createdAt, filterMonth)).length;

  const completedInMonth = list.filter(
    (p) => p.status === 'completed' && p.createdAt && isInSelectedMonth(p.completedAt, filterMonth)
  );
  let avgDays = '—';
  if (completedInMonth.length > 0) {
    const totalMs = completedInMonth.reduce((sum, p) => {
      const ms = new Date(p.completedAt).getTime() - new Date(p.createdAt).getTime();
      return sum + Math.max(0, ms);
    }, 0);
    avgDays = (totalMs / completedInMonth.length / (24 * 60 * 60 * 1000)).toFixed(1);
  }
  return { submitted, avgDays };
}
